import type { AdminIdentity } from "@superadmin/utils/admin.loader";
import { authClient } from "@superadmin/utils/auth.client";
import { BrandLogo } from "@voicely/ui-mui/components/BrandLogo";
import { Avatar } from "@voicely/ui-mui/data-display/Avatar";
import {
	List,
	ListItem,
	ListItemButton,
	ListItemIcon,
	ListItemText,
} from "@voicely/ui-mui/data-display/List";
import { Tooltip } from "@voicely/ui-mui/data-display/Tooltip";
import { Typography } from "@voicely/ui-mui/data-display/Typography";
import { CalendarTodayIcon } from "@voicely/ui-mui/icons/CalendarTodayIcon";
import { DashboardIcon } from "@voicely/ui-mui/icons/DashboardIcon";
import { ErrorOutlineIcon } from "@voicely/ui-mui/icons/ErrorOutlineIcon";
import { ListIcon } from "@voicely/ui-mui/icons/ListIcon";
import { LogoutIcon } from "@voicely/ui-mui/icons/LogoutIcon";
import { MenuIcon } from "@voicely/ui-mui/icons/MenuIcon";
import { PostAddIcon } from "@voicely/ui-mui/icons/PostAddIcon";
import { SearchIcon } from "@voicely/ui-mui/icons/SearchIcon";
import { SettingsIcon } from "@voicely/ui-mui/icons/SettingsIcon";
import { SupportAgentIcon } from "@voicely/ui-mui/icons/SupportAgentIcon";
import { TableRowsIcon } from "@voicely/ui-mui/icons/TableRowsIcon";
import { TuneIcon } from "@voicely/ui-mui/icons/TuneIcon";
import { Box } from "@voicely/ui-mui/layout/Box";
import { Divider } from "@voicely/ui-mui/layout/Divider";
import { Stack } from "@voicely/ui-mui/layout/Stack";
import { Drawer } from "@voicely/ui-mui/navigation/Drawer";
import { IconButton } from "@voicely/ui-mui/navigation/IconButton";
import { type ReactNode, useState } from "react";
import { NavLink, Outlet, useLoaderData } from "react-router";

const DRAWER_WIDTH = 240;

/**
 * Flat on purpose. A dozen destinations do not need groups, and every one of
 * them is a page — there is no "current workspace" to switch, because every
 * page here is already cross-workspace.
 *
 * Costing sits next to Sessions because it answers the same question about the
 * same rows — which call, and what did it do — in money instead of latency.
 * Pricing follows it rather than living under Providers: the catalogue decides
 * which vendors a workspace may BUILD with, and the price book decides what
 * their usage is worth, which are different decisions made at different times.
 */
const NAV_ITEMS: { label: string; path: string; icon: ReactNode }[] = [
	{ label: "Dashboard", path: "/dashboard", icon: <DashboardIcon /> },
	{ label: "Sessions", path: "/sessions", icon: <ListIcon /> },
	{ label: "Costing", path: "/costing", icon: <CalendarTodayIcon /> },
	{ label: "Pricing", path: "/pricing", icon: <TableRowsIcon /> },
	{ label: "Phone numbers", path: "/phone-numbers", icon: <SupportAgentIcon /> },
	{ label: "Providers", path: "/providers", icon: <TuneIcon /> },
	{ label: "Workspaces", path: "/teams", icon: <TableRowsIcon /> },
	{ label: "Tenancy audit", path: "/tenancy-audit", icon: <ErrorOutlineIcon /> },
	{ label: "Feature flags", path: "/feature-flags", icon: <SettingsIcon /> },
	{ label: "Container env", path: "/container-env", icon: <SettingsIcon /> },
	{ label: "Agent config", path: "/agent-config", icon: <SearchIcon /> },
	{ label: "System prompt", path: "/system-prompt", icon: <PostAddIcon /> },
];

const NavList = ({ onNavigate }: { onNavigate?: () => void }) => (
	<List sx={{ px: 1 }}>
		{NAV_ITEMS.map((item) => (
			<ListItem key={item.path} disablePadding>
				<ListItemButton
					component={NavLink}
					to={item.path}
					onClick={onNavigate}
					sx={{
						borderRadius: 2,
						mb: 0.25,
						"&.active": {
							bgcolor: "primary.lighter",
							color: "primary.main",
							"& .MuiListItemIcon-root": { color: "primary.main" },
						},
					}}
				>
					<ListItemIcon sx={{ minWidth: 38 }}>{item.icon}</ListItemIcon>
					<ListItemText primary={item.label} slotProps={{ primary: { variant: "body2" } }} />
				</ListItemButton>
			</ListItem>
		))}
	</List>
);

const DrawerHeader = () => (
	<Stack direction="row" spacing={1.25} alignItems="center" sx={{ px: 2.5, py: 2 }}>
		<BrandLogo variant="icon" height={24} alt="" />
		<Box>
			<Typography variant="subtitle1" sx={{ lineHeight: 1.2 }}>
				Voicely
			</Typography>
			<Typography variant="caption" color="text.secondary">
				Superadmin
			</Typography>
		</Box>
	</Stack>
);

/**
 * The frame around every operator page: a fixed nav rail, the operator's
 * identity, and sign-out. Nothing else — no workspace switcher (every page is
 * cross-workspace), no notification inbox, no install prompt.
 */
export const AdminLayout = () => {
	const { identity } = useLoaderData<{ identity: AdminIdentity }>();
	// Transient UI, like a menu anchor — not state the user navigates to, so
	// it is exempt from the URL-state rule.
	const [mobileOpen, setMobileOpen] = useState(false);

	const handleSignOut = async () => {
		await authClient.signOut();
		window.location.href = "/auth/login";
	};

	const drawerContent = (onNavigate?: () => void) => (
		<Stack sx={{ height: "100%" }}>
			<DrawerHeader />
			<Divider />
			<Box sx={{ flexGrow: 1, overflowY: "auto" }}>
				<NavList onNavigate={onNavigate} />
			</Box>
			<Divider />
			<Stack direction="row" spacing={1.25} alignItems="center" sx={{ p: 2 }}>
				<Avatar src={identity.image ?? undefined} sx={{ width: 32, height: 32 }}>
					{identity.name[0]}
				</Avatar>
				<Box sx={{ minWidth: 0, flexGrow: 1 }}>
					<Typography variant="body2" noWrap>
						{identity.name}
					</Typography>
					<Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
						{identity.email}
					</Typography>
				</Box>
				<Tooltip title="Sign out">
					<IconButton size="small" onClick={handleSignOut} aria-label="Sign out">
						<LogoutIcon fontSize="small" />
					</IconButton>
				</Tooltip>
			</Stack>
		</Stack>
	);

	return (
		<Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "surface.canvas" }}>
			<Drawer
				variant="permanent"
				sx={{
					display: { xs: "none", md: "block" },
					width: DRAWER_WIDTH,
					flexShrink: 0,
					"& .MuiDrawer-paper": { width: DRAWER_WIDTH, boxSizing: "border-box" },
				}}
			>
				{drawerContent()}
			</Drawer>

			<Drawer
				variant="temporary"
				open={mobileOpen}
				onClose={() => setMobileOpen(false)}
				sx={{
					display: { xs: "block", md: "none" },
					"& .MuiDrawer-paper": { width: DRAWER_WIDTH, boxSizing: "border-box" },
				}}
			>
				{drawerContent(() => setMobileOpen(false))}
			</Drawer>

			<Box sx={{ flexGrow: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
				<Box sx={{ display: { xs: "flex", md: "none" }, p: 1 }}>
					<IconButton onClick={() => setMobileOpen(true)} aria-label="Open navigation">
						<MenuIcon />
					</IconButton>
				</Box>
				<Outlet />
			</Box>
		</Box>
	);
};
